import { InlineLoading } from "@carbon/react";
import {
  Badge,
  CopyButton,
  FormContainer,
  LeftRightFlexedContainer,
  ModalSectionTitle,
  Select,
  SkeletonLoader,
  SwitchAppButton,
} from "../../../../components";
import { IManageTeamMemberTransferLimitsFormViewProps } from "./ManageTeamMemberTransferLimit";
import {
  HookFormSelect,
  SubmitButton,
  TextArea,
  TextInput,
} from "../../../../hook-form";
import {
  camelCaseToCapitalized,
  formatAsCurrency,
  getCurrencySymbol,
} from "../../../../utils";
import {
  approvalManagementTypes,
  approvalRequiredTypes,
} from "../../../../enums";

export const View = ({
  isLoadingBalances,
  balances,
  limitCurrency,

  clearTeamMemberTransferLimit,
  isLoadingGetTransferLimitsByCurrency,
  isErrorGetTransferLimitsByCurrency,
  teamMemberTransferLimitsByCurrency,

  // values used for settings form
  isLoadingAvailableAccessClaims,
  isErrorAvailableAccessClaims,
  isLoadingTeamMembers,
  isErrorTeamMembers,
  availableAccessClaims,
  approverStaffListOptions,

  teamMember,

  discloseLimitForm,
  formValues,

  staffIdToAdd,
  setStaffIdToAdd,
  roleToAdd,
  setRoleToAdd,
  approverStaffListFieldArrayContext,
  approverRolesListFieldArrayContext,
  appendStaffToApproverList,
  appendRoleToApproverList,

  errors,
  handleSubmit,
  register,
}: IManageTeamMemberTransferLimitsFormViewProps) => {
  return (
    <form onSubmit={handleSubmit}>
      <div className="manage-team-member-limits--container">
        <ModalSectionTitle key_id="Manage User Transfer Limits" />
      </div>

      <div className="flex-box">
        {/* <HookFormSelect
              name="currency"
              label="Currency"
              infoIconText="The currency of the withdrawal bank account eg USD"
              showSkeleton={isLoadingSupportedCurrencies}
              selectItems={swAppSupportedCurrencies.map((cur) => ({
                text: `${cur.currency_name} (${cur.currency_symbol})`,
                value: cur.currency,
              }))}
              register={register}
              errors={errors}
              disabled={!isActive || !formValues.country}
            /> */}
        <HookFormSelect
          name="currency"
          label="Currency (Wallet) to Set Transfer Limit"
          infoIconText="The wallet to set transfer limits for. This depends on the currencies you have enabled."
          showSkeleton={isLoadingBalances}
          selectItems={balances.map((bal) => ({
            text: `${bal.currency} (${getCurrencySymbol(
              bal.currency
            )}) Transfer Limit`,
            value: bal.currency,
          }))}
          register={register}
          errors={errors}
        />
      </div>

      {!discloseLimitForm.isOpen ? (
        // a toggle button to add a limit for a team member if one doesn't exist
        <div className="flex-container mt-5 align-items--center">
          <span className="flex-box flex-1_5">
            {teamMemberTransferLimitsByCurrency ? (
              <>
                <span className="text-bold">
                  {teamMemberTransferLimitsByCurrency.currency}
                </span>{" "}
                transfer limits are set for{" "}
                <span className="text-bold">{teamMember?.full_name}</span>.
              </>
            ) : (
              `No ${limitCurrency.toUpperCase()} transfer limits set for this user.`
            )}
          </span>

          <div className="flex-container justify-content--end">
            <SwitchAppButton
              className="switchapp-themed"
              onClick={discloseLimitForm.onOpen}
            >
              {teamMemberTransferLimitsByCurrency
                ? "Update Transfer Limit"
                : "Set Transfer Limit"}
            </SwitchAppButton>

            {!teamMemberTransferLimitsByCurrency ? null : (
              <SwitchAppButton
                className="switchapp-themed ml-5"
                kind="danger"
                onClick={clearTeamMemberTransferLimit}
              >
                Delete Transfer Limit
              </SwitchAppButton>
            )}
          </div>
        </div>
      ) : (
        <>
          <h5 className="mt-5">
            Authorization Requirements (Transfers Require Approval By Other
            Staff Members)
          </h5>
          <HookFormSelect
            name="approval_required_type"
            label="Approval Required"
            selectItems={Object.values(approvalRequiredTypes).map((m) => ({
              text: camelCaseToCapitalized(m),
              value: m,
            }))}
            register={register}
            errors={errors}
          />

          {formValues.approval_required_type ===
          approvalRequiredTypes.none ? null : (
            <TextInput
              name="approval_count_required"
              label="Authorization Count Required"
              infoIconText="The number of different team members required to authorize a transfer from this user. e.g. 2 means two different team members must authorize a transfer before it can be processed."
              placeholder="0 means no authorization required"
              type="number"
              register={register}
              errors={errors}
            />
          )}

          {formValues.approval_required_type ===
          approvalRequiredTypes.none ? null : (
            <>
              <HookFormSelect
                name="approval_management_type"
                label="Authorization Management Type"
                infoIconText="The type of authorization management to use for this transfer limit: by role or by staff member."
                selectItems={Object.values(approvalManagementTypes).map(
                  (m) => ({
                    text: camelCaseToCapitalized(m),
                    value: m,
                  })
                )}
                register={register}
                errors={errors}
              />

              {formValues.approval_management_type ===
              approvalManagementTypes.role ? (
                <>
                  {/* Roles List */}
                  <div className="flex-container justify-content--between align-items--end mt-5">
                    <div className="flex-box">
                      <Select
                        name="availableAccessClaimsSelectHelper"
                        label="Setup Roles That Can Authorize Transfers For This User"
                        infoIconText="Select a role to add to the authorizer roles list. All users with this role can authorize transfers initiated by this user."
                        placeholder="Please select a role"
                        showSkeleton={isLoadingAvailableAccessClaims}
                        selectItems={[]}
                        selectGroupedItems={[
                          {
                            groupLabel: ``,
                            options: [
                              {
                                text: "SELECT A ROLE",
                                value: "", // placeholder to escape first item selection bug
                              },
                            ],
                          },
                          ...availableAccessClaims
                            .filter((claim) =>
                              approverRolesListFieldArrayContext.fields.every(
                                (field) => field.value !== claim.claim
                              )
                            ) // filter out already added roles
                            .map((claim) => ({
                              groupLabel: `${claim.description}`,
                              options: [
                                {
                                  text: claim.claim,
                                  value: claim.claim,
                                },
                              ],
                            })),
                        ]}
                        onChange={(e) => setRoleToAdd(e.target?.value ?? null)}
                      />
                    </div>

                    <div className="flex-box">
                      <SwitchAppButton
                        size="sm"
                        kind={"primary"}
                        className="mt-5 ml-5 mr-5 pl-5 pr-5"
                        disabled={
                          !roleToAdd || availableAccessClaims.length < 1
                        }
                        onClick={appendRoleToApproverList} // NOTE: necessary to ignore post-data BUG from POST & PUT methods in useQuickSubmitFormHelper()
                      >
                        Add To Approve Transfers List
                      </SwitchAppButton>
                    </div>
                  </div>

                  <div className="flex-container">
                    {approverRolesListFieldArrayContext.fields.map(
                      (claimWithIdEntity, index) => (
                        <div
                          key={claimWithIdEntity.id}
                          className="mr-5 cursor-pointer"
                          title="Click to remove role"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Are you sure you want to remove this role from 'Approvers List'?"
                              )
                            ) {
                              approverRolesListFieldArrayContext.remove(index);
                            }
                          }}
                        >
                          <Badge
                            text={claimWithIdEntity.value}
                            statusColor={
                              claimWithIdEntity.value
                                .toLowerCase()
                                .includes("owner")
                                ? "teal"
                                : claimWithIdEntity.value
                                    .toLowerCase()
                                    .includes("admin")
                                ? "primary"
                                : "warning"
                            }
                            className="pl-5 mt-5"
                            indicatorComponent={
                              <span className="pl-5 text-danger font-ctm--bold">
                                &times;
                              </span>
                            }
                          />
                        </div>
                      )
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Staff List */}

                  <div className="flex-container justify-content--between align-items--end mt-5">
                    <div className="flex-box">
                      <Select
                        name="availableStaffIdsSelectHelper"
                        label="Setup Staff That Can Authorize Transfers For This User"
                        infoIconText="Select a staff member to add to the authorizer list. The selected authorizer staff can authorize transfers initiated by this user."
                        placeholder="Please select a team member"
                        showSkeleton={isLoadingTeamMembers}
                        selectItems={[]}
                        selectGroupedItems={[
                          {
                            groupLabel: ``,
                            options: [
                              {
                                text: "Select a staff to approve user's transfers",
                                value: "", // placeholder to escape first item selection bug
                              },
                            ],
                          },
                          ...approverStaffListOptions
                            .filter((staff) =>
                              approverStaffListFieldArrayContext.fields.every(
                                (field) => field.value !== staff.id
                              )
                            ) // filter out already added roles
                            .map((staff) => ({
                              groupLabel: `${staff.full_name}`,
                              options: [
                                {
                                  text: staff.full_name,
                                  value: staff.id,
                                },
                              ],
                            })),
                        ]}
                        onChange={(e) =>
                          setStaffIdToAdd(e.target?.value ?? null)
                        }
                      />
                    </div>

                    <div className="flex-box">
                      <SwitchAppButton
                        size="sm"
                        kind={"primary"}
                        className="mt-5 ml-5 mr-5 pl-5 pr-5"
                        disabled={
                          !staffIdToAdd || approverStaffListOptions.length < 1
                        }
                        onClick={appendStaffToApproverList} // NOTE: necessary to ignore post-data BUG from POST & PUT methods in useQuickSubmitFormHelper()
                      >
                        Add To Approve Transfers List
                      </SwitchAppButton>
                    </div>
                  </div>

                  <div className="flex-container">
                    {approverStaffListFieldArrayContext.fields.map(
                      (staffIdWithIdEntity, index) => (
                        <div
                          key={staffIdWithIdEntity.id}
                          className="mr-5 cursor-pointer"
                          title="Click to remove team member"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Are you sure you want to remove this team member from 'Authorizers List'?"
                              )
                            ) {
                              approverStaffListFieldArrayContext.remove(index);
                            }
                          }}
                        >
                          <Badge
                            text={
                              approverStaffListOptions.find(
                                (stf) => stf.id === staffIdWithIdEntity.value
                              )?.full_name ?? "Unknown Staff"
                            }
                            statusColor={true ? "primary" : "warning"}
                            className="pl-5 mt-5"
                            indicatorComponent={
                              <span className="pl-5 text-danger font-ctm--bold">
                                &times;
                              </span>
                            }
                          />
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
            </>
          )}

          <h5 className="mt-5">
            Volume Limits (By Sum Total Amount of Transfers)
          </h5>

          <div className="flex-container flex-break--small">
            <div className="flex-box">
              <TextInput
                name="single_transfer_amount_limit"
                label="Max Single Transfer Amount"
                infoIconText="The maximum amount allowed for a single transfer."
                type="number"
                register={register}
                errors={errors}
              />
            </div>

            <div className="flex-box ml-md-5">
              <TextInput
                name="daily_amount_limit"
                label="Daily Amount Limit"
                infoIconText="The maximum amount allowed for transfers within a single day."
                type="number"
                register={register}
                errors={errors}
              />
            </div>
          </div>

          <div className="flex-container flex-break--small">
            <div className="flex-box">
              <TextInput
                name="monthly_amount_limit"
                label="Monthly Amount Limit"
                infoIconText="The maximum amount allowed for transfers within a single month."
                type="number"
                register={register}
                errors={errors}
              />
            </div>

            <div className="flex-box ml-md-5">
              <TextInput
                name="yearly_amount_limit"
                label="Yearly Amount Limit"
                infoIconText="The maximum amount allowed for transfers within a single year."
                type="number"
                register={register}
                errors={errors}
              />
            </div>
          </div>

          <h5 className="mt-5">Count Limits (By Count Of Transfers)</h5>

          <div className="flex-container flex-break--small">
            <div className="flex-box">
              <TextInput
                name="daily_count_limit"
                label="Daily Count Limit"
                infoIconText="The maximum count allowed for transfers within a single day."
                type="number"
                register={register}
                errors={errors}
              />
            </div>

            <div className="flex-box ml-md-5"></div>
          </div>

          <div className="flex-container flex-break--small">
            <div className="flex-box">
              <TextInput
                name="monthly_count_limit"
                label="Monthly Count Limit"
                infoIconText="The maximum count allowed for transfers within a single month."
                type="number"
                register={register}
                errors={errors}
              />
            </div>

            <div className="flex-box ml-md-5">
              <TextInput
                name="yearly_count_limit"
                label="Yearly Count Limit"
                infoIconText="The maximum count allowed for transfers within a single year."
                type="number"
                register={register}
                errors={errors}
              />
            </div>
          </div>

          <TextArea
            name="description"
            label="Limit Description (Optional)"
            infoIconText="More info about this limit on this staff member."
            register={register}
            errors={errors}
          />

          <div className="flex-container buttons-container mt-5">
            <SubmitButton className="flex-box switchapp-themed">
              Set/Update Transfer Limit
            </SubmitButton>
          </div>
        </>
      )}
    </form>
  );
};
